import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import heroDashboard from "@/assets/landing/hero-ai-platform.webp";

export function LandingHero() {
  const { t } = useTranslation("landing");

  return (
    <section className="relative min-h-[90vh] flex flex-col items-center justify-center pt-16 pb-8">
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-sm text-muted-foreground tracking-widest uppercase mb-6"
        >
          {t("hero.badge")}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight text-foreground leading-[1.05]"
        >
          {t("hero.headline1")}
          <br />
          <span className="text-muted-foreground">{t("hero.headline2")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
        >
          {t("hero.subhead")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          <Button size="lg" className="gap-2 h-12 px-8 text-base" asChild>
            <Link to="/auth?tab=signup">
              {t("hero.ctaPrimary")} <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-12 px-8 text-base"
            onClick={() => document.querySelector("#pricing")?.scrollIntoView({ behavior: "smooth" })}
          >
            {t("hero.ctaSecondary")}
          </Button>
        </motion.div>
      </div>

      {/* Hero Dashboard Preview */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="relative z-10 mt-16 w-full max-w-5xl mx-auto px-6"
      >
        <div className="relative rounded-xl overflow-hidden border border-border/50 shadow-2xl shadow-primary/5">
          <img
            src={heroDashboard}
            alt="Lombrici AI Platform Dashboard"
            className="w-full h-auto"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.9 }}
        className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-muted-foreground"
      >
        {[
          { value: "11", label: t("hero.stats.modules") },
          { value: "4", label: t("hero.stats.suites") },
          { value: "€99", label: t("hero.stats.from") },
        ].map((s) => (
          <div key={s.label} className="text-center">
            <span className="text-2xl font-semibold text-foreground">{s.value}</span>
            <p className="text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
